import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import UIDefaultTheme from 'src/themes';
import { AuthService } from '../auth/auth.service';

export interface BreadcrumbItem {
  label: string;
  url: string;
  params?: any;
  clickable?: boolean; // Se false, o item não será clicável (depende da permissão do usuário)
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private breadcrumbsSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  public breadcrumbs$ = this.breadcrumbsSubject.asObservable();

  private readonly STORAGE_KEY = 'breadcrumb_history';
  private readonly MENU_ROUTES = [
    '/app/dashboard',
    '/app/accounts',
    '/app/invites',
    '/app/registers',
    '/app/funds',
    '/app/settings',
    '/app/permissions',
    '/app/drafts',
    '/app/company',
  ];

  // Mapeamento de rotas para labels amigáveis
  private readonly ROUTE_LABELS = {
    permissions: 'Permissões',
    list: 'Controle de permissões',
    companies: 'Empresas',
    details: 'Detalhes',
    groups: 'Perfis de acessos',
    users: 'Usuários',
    user: 'Usuários',
    new: 'Novo',
    update: 'Editar',
    witnesses: 'Testemunhas',
    settings: 'Configurações',
    accounts: 'Meus cadastros',
    invites: 'Convites',
    drafts: 'Minutas',
    funds: 'Fundos',
    security: 'Segurança',
    dashboard: 'Dashboard',
    registers: 'Cadastros',
    'request-access': 'Solicitações de acesso',
    bond: 'Vincular perfis',
    resume: 'Resumo',
    group: 'Grupo',
    history: 'Histórico',
    approval: 'Aprovação',
    documents: 'Documentos',
    agents: 'Agentes',
    audits: 'Auditorias',
    types: 'Tipos',
    variables: 'Variáveis',
    models: 'Modelos',
    bonds: 'Vínculos',
    versions: 'Versões',
    form: 'Formulário',
    onboarding: 'Onboarding',
    provider: 'Prestador de serviço',
    assignor: 'Cedente',
    shareholder: 'Cotista',
  };

  // Mapeamento de contextos específicos
  private readonly CONTEXT_LABELS = {
    companies: 'Detalhes da empresa',
    user: 'Detalhes do usuário',
    groups: 'Detalhes do grupo',
    permissions: 'Detalhes da permissão',
    funds: 'Detalhes do fundo',
  };

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    this.initializeService();
  }

  // ===== MÉTODOS PÚBLICOS =====

  public navigateToBreadcrumb(breadcrumb: BreadcrumbItem): void {
    this.router.navigateByUrl(breadcrumb.url);
    this.truncateBreadcrumbTo(breadcrumb.url);
  }

  public getCurrentBreadcrumbs(): BreadcrumbItem[] {
    return this.breadcrumbsSubject.value;
  }

  public clearBreadcrumb(): void {
    this.resetBreadcrumb();
    localStorage.removeItem(this.STORAGE_KEY);
  }

  public initializeBreadcrumb(): void {
    // Verificar a URL atual antes de carregar do storage
    const currentUrl = this.router.url;
    
    // Se for uma rota de detalhes, só carregar se o usuário estiver disponível
    if (this.isDetailsRouteWithRole(currentUrl)) {
      const user = this.authService.getUser();
      if (user && user.active_register) {
        // Usuário disponível, pode carregar do storage ou construir novo
        this.loadBreadcrumbFromStorage();
      } else {
        // Usuário não disponível, não carregar breadcrumb antigo
        // Isso evita mostrar "Meu departamento" antes de saber o tipo de usuário
        this.resetBreadcrumb();
      }
    } else if (currentUrl === '/app/dashboard' || currentUrl.startsWith('/app/dashboard/')) {
      // Para /app/dashboard, verificar se o usuário pode ver antes de carregar
      const user = this.authService.getUser();
      if (user && user.active_register) {
        // Usuário disponível, pode carregar do storage mas filtrando "Meu departamento"
        this.loadBreadcrumbFromStorage();
      } else {
        // Usuário não disponível, não carregar breadcrumb antigo
        // Isso evita mostrar "Meu departamento" antes de saber o tipo de usuário
        this.resetBreadcrumb();
      }
    } else {
      // Para outras rotas, carregar normalmente do storage
      this.loadBreadcrumbFromStorage();
    }
  }

  /**
   * Define breadcrumbs manualmente (usado quando cada tela define seus próprios breadcrumbs)
   * @param breadcrumbs Array de breadcrumbs a serem definidos
   */
  public setBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
    // Garantir que todos os items tenham clickable definido (padrão: true)
    const breadcrumbsWithDefaults = breadcrumbs.map(item => ({
      ...item,
      clickable: item.clickable !== undefined ? item.clickable : true
    }));
    this.updateBreadcrumbs(breadcrumbsWithDefaults);
    this.saveBreadcrumbToStorage();
  }

  // ===== MÉTODOS PRIVADOS - INICIALIZAÇÃO =====

  private initializeService(): void {
    this.clearEmptyBreadcrumbs();
    this.subscribeToRouterEvents();
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleNavigation(event.url);
      });
  }

  // ===== MÉTODOS PRIVADOS - NAVEGAÇÃO =====

  private handleNavigation(url: string): void {
    const path = this.getPathWithoutQuery(url);

    if (this.isMenuRoute(path)) {
      if (path === '/app/drafts') {
        const current = this.breadcrumbsSubject.value;
        const hasDraftsBreadcrumb =
          current.length > 0 &&
          current[0].url.split('?')[0] === '/app/drafts';

        if (hasDraftsBreadcrumb) {
          this.saveBreadcrumbToStorage();
          return;
        }
      }

      // Para /app/dashboard, verificar se pode ver "Meu departamento" antes de adicionar
      if (url === '/app/dashboard') {
        const user = this.authService.getUser();
        if (user && user.active_register) {
          // Usuário disponível, verificar se pode ver
          if (this.canViewMyDepartment()) {
            this.resetBreadcrumb();
            this.addBreadcrumbItem(path);
          } else {
            // Não pode ver "Meu departamento", limpar breadcrumb
            this.resetBreadcrumb();
          }
        } else {
          // Usuário não disponível ainda, limpar breadcrumb para evitar mostrar "Meu departamento"
          this.resetBreadcrumb();
        }
      } else {
        // Para outras rotas de menu, adicionar normalmente
        this.resetBreadcrumb();
        this.addBreadcrumbItem(path);
      }
    } else if (this.isDetailsRouteWithRole(path)) {
      // Para rotas de detalhes (provider, shareholder, assignor), construir breadcrumb do zero
      // Mas só se já soubermos o tipo de usuário (master ou comum)
      const user = this.authService.getUser();
      if (user && user.active_register) {
        // Usuário está disponível, podemos construir o breadcrumb
        this.buildDetailsBreadcrumb(path);
      } else {
        // Usuário ainda não está disponível, limpar breadcrumb para evitar mostrar "Meu departamento"
        this.resetBreadcrumb();
      }
    } else {
      this.loadBreadcrumbFromStorage();
      this.addBreadcrumbItem(path);
    }
    this.saveBreadcrumbToStorage();
  }

  private getPathWithoutQuery(url: string): string {
    return url.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  }

  private isMenuRoute(url: string): boolean {
    const path = this.getPathWithoutQuery(url);
    return this.MENU_ROUTES.includes(path);
  }

  private isDetailsRouteWithRole(url: string): boolean {
    // Verificar se é uma rota de detalhes com papel (provider, shareholder, assignor)
    const detailsPattern =
      /^\/app\/details\/(provider|shareholder|assignor|representative)\//;
    return detailsPattern.test(url);
  }

  private buildDetailsBreadcrumb(url: string): void {
    // Construir breadcrumb correto para rotas de detalhes
    this.resetBreadcrumb();

    // Verificar se o usuário é cedente, cotista ou prestador de serviço
    const isSelfView = this.isSelfViewingRegister(url);
    const breadcrumbLabel = isSelfView ? 'Meu registro' : 'Cadastros';
    const breadcrumbUrl = isSelfView ? url : '/app/registers';

    // Adicionar o primeiro item (Meu registro ou Cadastros)
    if (isSelfView) {
      // Para self view, adicionar "Meu registro" como item não clicável
      const breadcrumbItem: BreadcrumbItem = {
        label: breadcrumbLabel,
        url: breadcrumbUrl,
        clickable: false,
      };
      const currentBreadcrumbs = this.breadcrumbsSubject.value;
      this.breadcrumbsSubject.next([...currentBreadcrumbs, breadcrumbItem]);
    } else {
      this.addBreadcrumbItem('/app/registers');
    }

    // Adicionar o item de detalhes
    this.addBreadcrumbItem(url);
  }

  /**
   * Verifica se o usuário está visualizando seu próprio registro
   * (cedente, cotista ou prestador de serviço que não é colaborador comum ou master)
   */
  private isSelfViewingRegister(url: string): boolean {
    const user = this.authService.getUser();
    if (!user || !user.active_register) {
      return false;
    }

    // Verificar se NÃO é colaborador comum ou master
    const isCommonCollaborator = this.authService.isCommonCollaborator();
    const isMaster = this.authService.isMasterUser();

    if (isCommonCollaborator || isMaster) {
      return false;
    }

    // Extrair o role e ID da URL
    const urlMatch = url.match(/^\/app\/details\/(provider|shareholder|assignor)\/(.+)$/);
    if (!urlMatch) {
      return false;
    }

    const roleFromUrl = urlMatch[1];
    const idFromUrl = urlMatch[2];
    const activeRegister = user.active_register.register;

    // Verificar se o role da URL corresponde ao role do usuário
    const isRoleMatch = activeRegister.role?.applicable === roleFromUrl;

    // Verificar se o ID da URL corresponde ao ID do registro ativo do usuário
    const isIdMatch = activeRegister.id === idFromUrl;

    return isRoleMatch && isIdMatch;
  }

  /**
   * Verifica se o usuário pode ver "Meu departamento"
   * Só pode ver se for master, owner ou usuário comum (não prestador, cedente ou cotista)
   */
  private canViewMyDepartment(): boolean {
    const user = this.authService.getUser();
    if (!user) {
      return false;
    }

    // Se for master ou owner, pode ver
    if (this.authService.isMasterUser()) {
      return true;
    }

    // Se não tiver active_register, não pode ver
    if (!user.active_register) {
      return false;
    }

    // Se for colaborador comum, pode ver
    if (this.authService.isCommonCollaborator()) {
      return true;
    }

    // Prestador, cedente ou cotista não podem ver
    const activeRegister = user.active_register.register;
    const roleApplicable = activeRegister.role?.applicable;
    
    // Se for prestador, cedente ou cotista, não pode ver
    if (roleApplicable === 'provider' || 
        roleApplicable === 'assignor' || 
        roleApplicable === 'shareholder') {
      return false;
    }

    // Por padrão, pode ver (casos que não se enquadram acima)
    return true;
  }

  // ===== MÉTODOS PRIVADOS - GESTÃO DE BREADCRUMBS =====

  private addBreadcrumbItem(url: string): void {
    const label = this.getBreadcrumbLabel(url);

    if (!this.isValidLabel(label)) return;

    // Se o label for "Meu departamento" e o usuário não pode ver, não adicionar
    if (label === 'Meu departamento' && !this.canViewMyDepartment()) {
      return;
    }

    const breadcrumbItem = this.createBreadcrumbItem(url, label);
    const currentBreadcrumbs = this.breadcrumbsSubject.value;

    if (this.breadcrumbExists(currentBreadcrumbs, url)) {
      this.handleExistingBreadcrumb(currentBreadcrumbs, url);
    } else {
      this.addNewBreadcrumb(currentBreadcrumbs, breadcrumbItem);
    }
  }

  private createBreadcrumbItem(url: string, label: string): BreadcrumbItem {
    return {
      label: label.trim(),
      url,
      params: this.extractParams(url),
    };
  }

  private breadcrumbExists(
    breadcrumbs: BreadcrumbItem[],
    url: string
  ): boolean {
    return breadcrumbs.some((item) => item.url === url);
  }

  private handleExistingBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    url: string
  ): void {
    const existingIndex = breadcrumbs.findIndex((item) => item.url === url);
    const newBreadcrumbs = breadcrumbs.slice(0, existingIndex + 1);
    this.updateBreadcrumbs(this.filterValidBreadcrumbs(newBreadcrumbs));
  }

  private addNewBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    newItem: BreadcrumbItem
  ): void {
    const newBreadcrumbs = [...breadcrumbs, newItem];
    this.updateBreadcrumbs(this.filterValidBreadcrumbs(newBreadcrumbs));
  }

  private truncateBreadcrumbTo(url: string): void {
    const currentBreadcrumbs = this.breadcrumbsSubject.value;
    const clickedIndex = currentBreadcrumbs.findIndex(
      (item) => item.url === url
    );

    if (clickedIndex >= 0) {
      const newBreadcrumbs = currentBreadcrumbs.slice(0, clickedIndex + 1);
      this.updateBreadcrumbs(newBreadcrumbs);
      this.saveBreadcrumbToStorage();
    }
  }

  private updateBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
    this.breadcrumbsSubject.next(breadcrumbs);
  }

  private resetBreadcrumb(): void {
    this.breadcrumbsSubject.next([]);
  }

  // ===== MÉTODOS PRIVADOS - LABELS E MAPEAMENTO =====

  private getBreadcrumbLabel(url: string): string {
    const segments = this.getPathSegments(url);
    if (segments.length === 0) return '';

    // Primeiro, tentar obter o label do routing
    const routeLabel = this.getRouteLabel(url);
    if (routeLabel) {
      return routeLabel;
    }

    const lastSegment = segments[segments.length - 1];

    // Verificar se é um ID/token
    if (this.isIdOrToken(lastSegment) && segments.length > 1) {
      return this.getContextualLabel(segments[segments.length - 2], segments);
    }

    // Verificar casos especiais
    const specialLabel = this.getSpecialCaseLabel(segments);
    if (specialLabel) {
      return specialLabel;
    }

    // Verificar se é um segmento "details" e tratar adequadamente
    if (lastSegment === 'details' && segments.length > 1) {
      return this.getDetailsLabelFromContext(segments);
    }

    // Mapeamento padrão
    return this.getDefaultLabel(lastSegment);
  }

  private getRouteLabel(url: string): string | null {
    try {
      // Tentar encontrar a rota correspondente no router
      const route = this.findRouteByUrl(url);
      return route?.data?.breadcrumb || null;
    } catch (error) {
      return null;
    }
  }

  private findRouteByUrl(url: string): any {
    // Implementação simplificada para encontrar a rota
    // Por enquanto, vamos usar um mapeamento direto baseado no app.routing.ts
    const routeMap: { [key: string]: string } = {
      // Permissões
      '/app/permissions': 'Permissões',
      '/app/permissions/list': 'Controle de permissões',
      '/app/permissions/list/details': 'Detalhes do grupo de permissão',
      '/app/permissions/list/new': 'Novo grupo de permissões',
      '/app/permissions/list/update': 'Editar grupo de permissões',
      '/app/permissions/witnesses': 'Testemunhas',
      '/app/permissions/companies': 'Empresas',
      '/app/permissions/companies/details': 'Detalhes da empresa',
      '/app/permissions/companies/bond': 'Vincular perfis',
      '/app/permissions/companies/bond/resume': 'Resumo',
      '/app/permissions/companies/group/details': 'Detalhes do grupo',
      '/app/permissions/companies/user/details': 'Detalhes de usuário',
      '/app/permissions/groups': 'Perfis de acessos',
      '/app/permissions/groups/new': 'Novo grupo',
      '/app/permissions/groups/update': 'Editar grupo',
      '/app/permissions/groups/user/details': 'Detalhes do usuário',
      '/app/permissions/groups/user/details/history': 'Histórico',

      // Fundos
      '/app/funds': 'Fundos',
      '/app/funds/new': 'Novo Fundo',
      '/app/funds/approval': 'Aprovação',
      '/app/funds/edit': 'Editar Fundo',
      '/app/funds/approval/model': 'Modelo',

      // Minutas
      '/app/drafts': 'Minutas',
      '/app/drafts/types': 'Tipos',
      '/app/drafts/types/new': 'Novo tipo',
      '/app/drafts/types/update': 'Editar tipo',
      '/app/drafts/bonds': 'Vínculos',
      '/app/drafts/bonds/new': 'Novo vínculo',
      '/app/drafts/bonds/update': 'Editar vínculo',
      '/app/drafts/models': 'Modelos',
      '/app/drafts/models/new': 'Novo modelo',
      '/app/drafts/models/update': 'Editar modelo',
      '/app/drafts/variables': 'Variáveis',

      // Outras rotas principais
      '/app/dashboard': 'Meu departamento',
      '/app/accounts': 'Meus cadastros',
      '/app/invites': 'Convites',
      '/app/registers': 'Cadastros',
      '/app/settings': 'Configurações',
      '/app/company': UIDefaultTheme.brand.company_name || UIDefaultTheme.brand.name || 'Empresa',

      // Rotas específicas com termos corretos
      '/app/details/provider': 'Detalhes do prestador de serviço',
      '/app/details/assignor': 'Detalhes do cedente',
      '/app/details/shareholder': 'Detalhes do cotista',
      '/app/dashboard/assignor': 'Painel Cedente',
      '/app/dashboard/shareholder': 'Painel Cotista',
      '/app/dashboard/provider': 'Painel Prestador de Serviço',
      '/app/assignors': 'Painel Cedente',
      '/app/providers': 'Painel Prestador de Serviço',
      '/app/shareholders': 'Painel Cotistas',
    };

    // Normalizar a URL removendo IDs
    const normalizedUrl = this.normalizeUrl(url);

    // Verificar se é uma rota de detalhes/edição com ID
    if (this.isDetailsRoute(url)) {
      return this.getDetailsRouteLabel(url);
    }

    return routeMap[normalizedUrl]
      ? { data: { breadcrumb: routeMap[normalizedUrl] } }
      : null;
  }

  private isDetailsRoute(url: string): boolean {
    // Verificar se a URL contém um ID
    const hasId =
      /\/(\d+|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/.test(
        url
      );

    if (!hasId) return false;

    // Verificar se é uma rota de detalhes com papel (provider, shareholder, assignor, representative)
    if (
      /^\/app\/details\/(provider|shareholder|assignor|representative)\//.test(
        url
      )
    ) {
      return true;
    }

    // Mapeamento de padrões de rotas de detalhes
    const detailsPatterns = [
      // Minutas
      { pattern: '/drafts/', subPatterns: ['/types/', '/bonds/', '/models/'] },
      // Permissões
      {
        pattern: '/permissions/',
        subPatterns: [
          '/details/',
          '/companies/details/',
          '/companies/',
          '/groups/details/',
        ],
      },
      // Fundos
      { pattern: '/funds/', subPatterns: ['/approval/'] },
    ];

    return detailsPatterns.some(({ pattern, subPatterns }) => {
      if (!url.includes(pattern)) return false;

      return subPatterns.some((subPattern) => {
        if (subPattern === '/companies/') {
          return (
            url.includes('/group/details/') || url.includes('/user/details/')
          );
        }
        return url.includes(subPattern);
      });
    });
  }

  private getDetailsRouteLabel(url: string): any {
    // Verificar primeiro se é uma rota de detalhes com papel
    if (/^\/app\/details\/provider\//.test(url)) {
      return { data: { breadcrumb: 'Detalhes do prestador de serviço' } };
    }
    if (/^\/app\/details\/shareholder\//.test(url)) {
      return { data: { breadcrumb: 'Detalhes do cotista' } };
    }
    if (/^\/app\/details\/assignor\//.test(url)) {
      return { data: { breadcrumb: 'Detalhes do cedente' } };
    }
    if (/^\/app\/details\/representative\//.test(url)) {
      return { data: { breadcrumb: 'Detalhes do representante' } };
    }

    // Mapeamento de rotas de detalhes para labels
    const detailsRouteMap = [
      { pattern: '/drafts/types/', label: 'Detalhes de tipo' },
      { pattern: '/drafts/bonds/', label: 'Detalhes de vínculo' },
      { pattern: '/drafts/models/', label: 'Detalhes de modelo' },
      { pattern: '/drafts/variables/', label: 'Detalhes de variável' },
      {
        pattern: '/permissions/companies/',
        subPattern: '/user/details/',
        label: 'Detalhes de usuário',
      },
      {
        pattern: '/permissions/companies/',
        subPattern: '/group/details/',
        label: 'Detalhes de grupo de usuários',
      },
      {
        pattern: '/permissions/companies/details/',
        label: 'Detalhes da empresa',
      },
      { pattern: '/permissions/groups/details/', label: 'Detalhes do grupo' },
      {
        pattern: '/permissions/details/',
        label: 'Detalhes de grupo de permissão',
      },
      { pattern: '/funds/approval/', label: 'Detalhes do fundo' },
    ];

    for (const route of detailsRouteMap) {
      if (route.subPattern) {
        if (url.includes(route.pattern) && url.includes(route.subPattern)) {
          return { data: { breadcrumb: route.label } };
        }
      } else if (url.includes(route.pattern)) {
        return { data: { breadcrumb: route.label } };
      }
    }

    return null;
  }

  private normalizeUrl(url: string): string {
    // Remover IDs da URL para fazer o mapeamento
    let normalized = url
      // Remover UUIDs
      .replace(
        /\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/g,
        ''
      )
      // Remover números simples (IDs)
      .replace(/\/\d+/g, '')
      // Remover barras finais
      .replace(/\/$/, '');

    // Se a URL normalizada termina com 'details', manter apenas o padrão
    if (normalized.endsWith('/details')) {
      normalized = normalized.replace(/\/details$/, '/details');
    }

    return normalized;
  }

  private getSpecialCaseLabel(segments: string[]): string | null {
    if (segments.length < 2) return null;

    const previousSegment = segments[segments.length - 2];
    const lastSegment = segments[segments.length - 1];

    const specialCases = {
      funds: {
        new: 'Novo Fundo',
        edit: 'Editar Fundo',
        approval: 'Aprovação',
      },
      permissions: {
        companies: 'Empresas',
        groups: 'Perfis de acessos',
        list: 'Controle de permissões',
        witnesses: 'Testemunhas',
      },
      companies: {
        details: 'Detalhes da empresa',
        bond: 'Vincular perfis',
        resume: 'Resumo',
        user: 'Usuários',
      },
      groups: {
        user: 'Usuários',
        new: 'Novo grupo',
        update: 'Editar grupo',
      },
      user: {
        details: 'Detalhes do usuário',
        permissions: 'Permissões do usuário',
        groups: 'Grupos do usuário',
      },
      drafts: {
        types: 'Tipos',
        bonds: 'Vínculos',
        models: 'Modelos',
        variables: 'Variáveis',
      },
    };

    return specialCases[previousSegment]?.[lastSegment] || null;
  }

  private getContextualLabel(
    previousSegment: string,
    allSegments: string[]
  ): string {
    // Mapeamento específico baseado no contexto da URL
    const contextualMap = {
      companies: {
        details: 'Detalhes da empresa',
        bond: 'Vincular perfis',
        resume: 'Resumo da empresa',
        user: 'Usuários da empresa',
        groups: 'Grupos da empresa',
      },
      groups: {
        details: 'Detalhes do grupo',
        user: 'Usuários do grupo',
        permissions: 'Permissões do grupo',
      },
      user: {
        details: 'Detalhes do usuário',
        permissions: 'Permissões do usuário',
        groups: 'Grupos do usuário',
      },
      permissions: {
        details: 'Detalhes da permissão',
        groups: 'Grupos com permissão',
        users: 'Usuários com permissão',
      },
      funds: {
        details: 'Detalhes do fundo',
        approval: 'Aprovação do fundo',
        edit: 'Editar fundo',
        new: 'Novo fundo',
      },
    };

    // Tentar encontrar um mapeamento específico
    const contextMap = contextualMap[previousSegment];
    if (contextMap) {
      // Procurar por segmentos específicos na URL
      const matchingSegment = Object.entries(contextMap).find(([key]) =>
        allSegments.includes(key)
      );

      if (matchingSegment) {
        return matchingSegment[1] as string;
      }
    }

    // Fallback para casos genéricos
    const baseLabel = this.CONTEXT_LABELS[previousSegment];
    if (baseLabel) {
      return baseLabel;
    }

    // Se não encontrar nada específico, usar o segmento anterior como contexto
    return `Detalhes de ${this.capitalizeFirstLetter(previousSegment)}`;
  }

  private getDetailsLabel(previousSegment: string): string {
    // Se o previousSegment também é "details", não usar o fallback genérico
    if (previousSegment === 'details') {
      return 'Detalhes';
    }

    const detailsMap = {
      companies: 'Detalhes da empresa',
      groups: 'Detalhes do grupo',
      user: 'Detalhes do usuário',
      permissions: 'Detalhes da permissão',
      funds: 'Detalhes do fundo',
      // Adicionar mais mapeamentos conforme necessário
    };

    return (
      detailsMap[previousSegment] ||
      `Detalhes de ${this.capitalizeFirstLetter(previousSegment)}`
    );
  }

  private getDetailsLabelFromContext(segments: string[]): string {
    // Analisar toda a URL para determinar o contexto correto
    const urlString = segments.join('/');

    // Mapeamento de contextos para labels de detalhes
    const contextLabelMap = [
      { patterns: ['companies', 'user'], label: 'Detalhes de usuário' },
      { patterns: ['companies', 'group'], label: 'Detalhes do grupo' },
      { patterns: ['companies'], label: 'Detalhes da empresa' },
      { patterns: ['groups', 'user'], label: 'Detalhes do usuário' },
      { patterns: ['groups'], label: 'Detalhes do grupo' },
      { patterns: ['user'], label: 'Detalhes do usuário' },
      { patterns: ['permissions'], label: 'Detalhes da permissão' },
      { patterns: ['funds'], label: 'Detalhes do fundo' },
      { patterns: ['provider'], label: 'Detalhes do prestador de serviço' },
      { patterns: ['assignor'], label: 'Detalhes do cedente' },
      { patterns: ['shareholder'], label: 'Detalhes do cotista' },
      { patterns: ['types'], label: 'Detalhes de tipo' },
      { patterns: ['bonds'], label: 'Detalhes de vínculo' },
      { patterns: ['models'], label: 'Detalhes de modelo' },
      { patterns: ['variables'], label: 'Detalhes de variável' },
    ];

    // Encontrar o primeiro contexto que corresponde
    for (const context of contextLabelMap) {
      if (context.patterns.every((pattern) => urlString.includes(pattern))) {
        return context.label;
      }
    }

    // Fallback genérico
    return 'Detalhes';
  }

  private getDefaultLabel(segment: string): string {
    return this.ROUTE_LABELS[segment] || this.capitalizeFirstLetter(segment);
  }

  // ===== MÉTODOS PRIVADOS - UTILITÁRIOS =====

  private getPathSegments(url: string): string[] {
    return url.split('/').filter((segment) => segment && segment !== 'app');
  }

  private isIdOrToken(segment: string): boolean {
    return (
      /^\d+$/.test(segment) ||
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
        segment
      ) ||
      segment.length > 20
    );
  }

  private isValidLabel(label: string): boolean {
    return label && label.trim() !== '';
  }

  private filterValidBreadcrumbs(
    breadcrumbs: BreadcrumbItem[]
  ): BreadcrumbItem[] {
    return breadcrumbs.filter((item) => this.isValidLabel(item.label));
  }

  private capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private extractParams(url: string): any {
    const params: any = {};
    const segments = this.getPathSegments(url);

    for (let i = 0; i < segments.length; i++) {
      if (this.isIdOrToken(segments[i]) && i > 0) {
        const paramName = this.getParamName(segments, i);
        params[paramName] = segments[i];
      }
    }

    return params;
  }

  private getParamName(segments: string[], idIndex: number): string {
    if (idIndex > 0) {
      const previousSegment = segments[idIndex - 1];
      const paramMap = {
        companies: 'companyId',
        user: 'userId',
        groups: 'groupId',
        permissions: 'permissionId',
      };
      return paramMap[previousSegment] || 'id';
    }
    return 'id';
  }

  // ===== MÉTODOS PRIVADOS - STORAGE =====

  private saveBreadcrumbToStorage(): void {
    const breadcrumbs = this.breadcrumbsSubject.value;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(breadcrumbs));
  }

  private loadBreadcrumbFromStorage(): void {
    try {
      const currentBreadcrumbs = this.breadcrumbsSubject.value;
      if (currentBreadcrumbs.length === 0) {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const breadcrumbs = JSON.parse(stored);
          
          // Filtrar breadcrumbs que não devem ser exibidos (como "Meu departamento" para prestador/cedente/cotista)
          const filteredByPermission = breadcrumbs.filter((item: BreadcrumbItem) => {
            // Se for "Meu departamento", verificar se pode ver
            if (item.label === 'Meu departamento') {
              return this.canViewMyDepartment();
            }
            return true;
          });
          
          // Verificar se o breadcrumb armazenado é de uma rota de detalhes
          const isDetailsBreadcrumb = filteredByPermission.some((item: BreadcrumbItem) => 
            this.isDetailsRouteWithRole(item.url)
          );
          
          // Se for breadcrumb de detalhes e o usuário não estiver disponível, não carregar
          if (isDetailsBreadcrumb) {
            const user = this.authService.getUser();
            if (!user || !user.active_register) {
              // Não carregar breadcrumb antigo se usuário não estiver disponível
              return;
            }
          }
          
          const filteredBreadcrumbs = this.filterValidBreadcrumbs(filteredByPermission);
          this.updateBreadcrumbs(filteredBreadcrumbs);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar breadcrumb do storage:', error);
    }
  }

  private clearEmptyBreadcrumbs(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const breadcrumbs = JSON.parse(stored);
        const filteredBreadcrumbs = this.filterValidBreadcrumbs(breadcrumbs);
        localStorage.setItem(
          this.STORAGE_KEY,
          JSON.stringify(filteredBreadcrumbs)
        );
      }
    } catch (error) {
      console.error('Erro ao limpar breadcrumbs vazios:', error);
    }
  }
}
